import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { ME_QUERY } from '~/graphql/queries/me';
import { type User, UserRole } from '~/shared/types';
import { parseRate } from '~/shared/utils/parseRate';

const API_ROLE_TO_USER_ROLE: Record<string, UserRole> = {
  attorney: UserRole.ATTORNEY,
  expert: UserRole.EXPERT,
  admin: UserRole.ADMIN,
};

export interface UseCurrentUserResult {
  user: User | null;
  isLoading: boolean;
}

export const useCurrentUser = (): UseCurrentUserResult => {
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const { data, loading: isQueryLoading } = useQuery(ME_QUERY, {
    skip: !isAuthenticated,
  });

  const isLoading = isAuth0Loading || isQueryLoading;

  if (!isAuthenticated || !data?.me) {
    return { user: null, isLoading };
  }

  const role =
    typeof data.me.role === 'string' && data.me.role in API_ROLE_TO_USER_ROLE
      ? API_ROLE_TO_USER_ROLE[data.me.role]
      : null;

  if (!role) {
    return { user: null, isLoading: false };
  }

  const expertPayload = data.me.expert;

  const user: User = {
    id: data.me.id,
    email: data.me.email,
    name: data.me.name ?? '',
    role,
    avatarUrl: expertPayload?.avatarUrl ?? undefined,
  };

  if (role === UserRole.EXPERT && expertPayload) {
    user.hourlyRate = parseRate(expertPayload.hourlyRate);
    user.depositionHourlyRate = parseRate(expertPayload.depositionHourlyRate);
    user.examinationHourlyRate = parseRate(expertPayload.examinationHourlyRate);
    user.recordsReviewHourlyRate = parseRate(
      expertPayload.recordsReviewHourlyRate,
    );
    user.testimonyHourlyRate = parseRate(expertPayload.testimonyHourlyRate);
    user.stripeConnectAccountId = expertPayload.stripeConnectAccountId;
    user.stripeConnectOnboarded = expertPayload.stripeConnectOnboarded;
  }

  return {
    user,
    isLoading: false,
  };
};
